import { AlertDialog, Button, DialogTrigger, Text } from '@react-spectrum/s2';
import Delete from '@react-spectrum/s2/icons/Delete';
import { useState } from 'react';

import type { StrategyGenerationProgressEvent } from '../../../../bridge/types/strategy.ts';

import { LiveDuration } from './StrategyBits';
import { timeAgo } from './strategyFormat';

// Phase labels.
const PHASE_LABEL: Record<string, string> = {
	researching: 'Researching the business, market, and pattern library…',
	drafting: 'Drafting the strategy…',
	validating: "Validating against Bridgeway's real capabilities…",
	complete: 'Complete',
	failed: 'Failed',
};

// Live research-activity feed while a run generates, poll-driven by the detail hook. The
// feed is an aria-live log so a screen reader hears new steps.
export function StrategyRunView({
	events,
	startedAt,
}: {
	events: StrategyGenerationProgressEvent[];
	startedAt: string;
}) {
	const phaseEvent = [...events].reverse().find((e) => e.type === 'phase');
	const phase = phaseEvent?.phase ?? 'researching';
	const steps = events.filter(
		(e) => e.type === 'research_step' || e.type === 'submit_attempt',
	);
	const narration = events.filter((e) => e.type === 'narration');
	const lastNarration = narration[narration.length - 1];

	return (
		<div
			className="scrollbar-hidden min-h-0 flex-1 space-y-6 overflow-y-auto pb-8"
		>
			<section className="space-y-1">
				<h1
					className="font-adobe-clean-display text-2xl font-bold tracking-tight
						text-(--text-primary)"
				>
					Building your strategy
				</h1>
				<p className="text-(--text-muted)">{PHASE_LABEL[phase] ?? phase}</p>
				{/* Outside the live region on purpose — a per-second tick must not be announced. */}
				<p className="text-sm text-(--text-muted)">
					Running for <LiveDuration startedAt={startedAt} />
				</p>
			</section>

			{lastNarration?.text && (
				<p className="glass-card-sm p-4 text-sm text-(--text-muted) italic">
					{lastNarration.text.slice(-400)}
				</p>
			)}

			<section
				role="log"
				aria-live="polite"
				aria-label="Research activity"
				className="glass-card scrollbar-hidden max-h-96 space-y-2 overflow-auto
					p-5"
			>
				{steps.length === 0 && (
					<p className="text-(--text-muted)">Waking the strategist…</p>
				)}
				{steps.map((e, i) =>
					e.type === 'research_step' ? (
						<p
							key={i}
							className="flex items-center gap-2 text-sm
								text-(--text-secondary)"
						>
							<span
								className="border-beacon-stroke inline-flex items-center
									rounded-full border bg-white px-2 py-0.5 font-mono text-xs"
							>
								{e.tool}
							</span>
							<span>
								{e.label}
								{e.leg ? (
									<span className="text-(--text-muted)"> (leg {e.leg})</span>
								) : null}
							</span>
						</p>
					) : (
						<p key={i} className="text-sm font-medium text-(--text-primary)">
							Submission attempt {e.n}:{' '}
							{e.violationCount === 0
								? 'clean'
								: `${e.violationCount} violations — revising`}
						</p>
					),
				)}
			</section>

			<p className="text-sm text-(--text-muted)">
				This run keeps going even if you leave the page — come back any time to
				check on it.
			</p>
		</div>
	);
}

// A failed run: the reason in an error banner + try-again/delete. `onDelete` is optional so
// a failed-only batch (which never reaches the completed view's header Delete) can still be cleared.
export function StrategyFailedView({
	error,
	startedAt,
	onGenerateAgain,
	onDelete,
}: {
	error: string | null | undefined;
	startedAt: string;
	// Rejects with owner-facing copy, which this view SHOWS. The server refuses a retry while a
	// sibling variant is still building, and the owner has to be told that or the press reads as
	// a dead button.
	onGenerateAgain: () => void | Promise<void>;
	onDelete?: () => Promise<void>;
}) {
	const [deleteError, setDeleteError] = useState<string | null>(null);
	const [retryError, setRetryError] = useState<string | null>(null);
	const [retrying, setRetrying] = useState(false);

	// `retrying` stays true after a success: the caller navigates away, so clearing it would
	// flash the control back to ready. Only a rejection returns the press to the owner.
	const retry = async () => {
		if (retrying) return;
		setRetryError(null);
		setRetrying(true);
		try {
			await onGenerateAgain();
		} catch (err) {
			setRetrying(false);
			setRetryError(
				err instanceof Error
					? err.message
					: "We couldn't start your strategy. Please try again.",
			);
		}
	};

	return (
		<div
			className="scrollbar-hidden min-h-0 flex-1 space-y-4 overflow-y-auto pb-8"
		>
			<div className="space-y-1">
				<h1
					className="font-adobe-clean-display text-2xl font-bold tracking-tight
						text-(--text-primary)"
				>
					Run failed
				</h1>
				{/* Static timeAgo — a terminal run is over, nothing to tick. */}
				<p className="text-sm text-(--text-muted)">
					Started {timeAgo(startedAt)}
				</p>
			</div>
			{/* red-700 on a red-50 tint, per design.md: red-600 measures 4.41:1 there and
			    fails the AA floor for body text. */}
			<p className="rounded-lg bg-red-50 px-4 py-3 text-red-700">
				{error ?? 'Something went wrong while building this strategy.'}
			</p>
			{/* States the two facts the control cannot: what it rebuilds, and when it can run. */}
			<p className="text-sm text-(--text-muted)">
				Generate again rebuilds only this variant. It can start once any other
				running variants finish building.
			</p>
			<div className="flex w-fit items-center gap-2">
				<Button
					variant="accent"
					isPending={retrying}
					isDisabled={retrying}
					onPress={() => void retry()}
				>
					<Text>Generate again</Text>
				</Button>
				{onDelete && (
					<DialogTrigger>
						<Button variant="negative" size="S">
							<Delete />
							<Text>Delete</Text>
						</Button>
						<AlertDialog
							variant="destructive"
							title="Delete this strategy?"
							primaryActionLabel="Delete"
							cancelLabel="Cancel"
							autoFocusButton="cancel"
							onPrimaryAction={async () => {
								setDeleteError(null);
								try {
									await onDelete();
								} catch (err) {
									setDeleteError(
										err instanceof Error
											? err.message
											: "We couldn't delete this strategy.",
									);
								}
							}}
						>
							This deletes this variant only. The other variants in this set
							stay as they are. This can&apos;t be undone.
						</AlertDialog>
					</DialogTrigger>
				)}
			</div>
			{retryError && (
				<p className="text-sm text-red-600" data-testid="generate-error">
					{retryError}
				</p>
			)}
			{deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
		</div>
	);
}
